import { Dialog, Tag, Typography } from '@cobliteam/ui/components';

import { Analytics } from '@cobliteam/analytics';
import type { Insight } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useInsights } from '@cobliteam/hooks';
import Icon from '@cobliteam/ui/icons';
import { addDays, startOfWeek, subDays, subWeeks } from '@cobliteam/utils/date/operations';
import { useEffect, useState } from 'react';
import insightsUtils from '../../utils';
import LandingContent from '../LandingContent';
import WeekInsights from '../WeekInsights/WeekInsights';
import InsightContent from './InsightContent';

interface InsightsProps {
  open: boolean;
  insight?: Insight;
  onOpen: (open: boolean) => void;
}

const TOTAL_WEEKS_TO_SHOW = 4;

const InsightsModal = ({ open, insight, onOpen }: InsightsProps) => {
  const { useGetInsights } = useInsights;
  const { markInsightAsRead, filterOldInsights, getReadInsights } = insightsUtils();

  const [readInsights, setReadInsights] = useState<{ [id: string]: string }>(getReadInsights());
  const [selectedInsight, setSelectedInsight] = useState<Insight | undefined>(undefined);
  const [isFirstInsight, setIsFirstInsight] = useState(false);
  const [isLastInsight, setIsLastInsight] = useState(false);

  useEffect(() => {
    setSelectedInsight(insight);
    if (insight) {
      const updatedReadInsights = markInsightAsRead(insight);
      setReadInsights(updatedReadInsights || getReadInsights());
    }
  }, [insight]);

  useEffect(() => {
    filterOldInsights(getReadInsights());
  }, []);

  const getWeekRequestPeriods = () => {
    const currentStartWeek = startOfWeek(new Date());

    return Array.from({ length: TOTAL_WEEKS_TO_SHOW }, (_, i) => i).map((i) => {
      const weekSundayStart = subDays(subWeeks(currentStartWeek, i), 1);
      const weekMondayEnd = addDays(weekSundayStart, 2);

      return { from: weekSundayStart, to: weekMondayEnd };
    });
  };

  const handleInsightClick = (insight: Insight) => {
    const updatedReadInsights = markInsightAsRead(insight);
    setReadInsights(updatedReadInsights);
    setSelectedInsight(insight);
    const lastWeek = allInsights[allInsights.length - 1];
    const lastInsight = lastWeek[lastWeek.length - 1];
    if (insight.id === lastInsight.id) {
      setIsFirstInsight(false);
      setIsLastInsight(true);
      return;
    }

    const firstWeek = allInsights[0];
    const firstInsight = firstWeek[0];
    if (insight.id === firstInsight.id) {
      setIsFirstInsight(true);
      setIsLastInsight(false);
      return;
    }

    setIsFirstInsight(false);
    setIsLastInsight(false);
  };

  const handleNextInsightClick = () => {
    if (!selectedInsight) return;

    Analytics.track('Insights Modal Next Button Clicked');

    setIsFirstInsight(false);

    const currentInsightWeekIndex = allInsights.findIndex((insights) => insights.includes(selectedInsight));
    const weekInsights = allInsights[currentInsightWeekIndex];
    const currentInsightIndex = weekInsights.findIndex((insight) => insight.id === selectedInsight.id);

    const isLastWeek = currentInsightWeekIndex === allInsights.length - 1;
    const isLastWeekInsight = currentInsightIndex === weekInsights.length - 1;

    // last insight from the week and has no more weeks
    if (isLastWeekInsight && isLastWeek) return;

    // last insight from week, but still have more
    if (isLastWeekInsight) {
      const nextWeekInsights = allInsights[currentInsightWeekIndex + 1];
      setSelectedInsight(nextWeekInsights[0]);
      const updatedReadInsights = markInsightAsRead(nextWeekInsights[0]);
      setReadInsights(updatedReadInsights);
      return;
    }

    // has more insights on current week
    const nextInsight = weekInsights[currentInsightIndex + 1];
    setSelectedInsight(nextInsight);
    const updatedReadInsights = markInsightAsRead(nextInsight);
    setReadInsights(updatedReadInsights);

    // is last week and next is last insight
    if (isLastWeek && currentInsightIndex === weekInsights.length - 2) {
      setIsLastInsight(true);
    }
  };

  const handlePreviousInsightClick = () => {
    if (!selectedInsight) return;

    Analytics.track('Insights Modal Previous Button Clicked');

    setIsLastInsight(false);

    const currentInsightWeekIndex = allInsights.findIndex((insights) => insights.includes(selectedInsight));
    const weekInsights = allInsights[currentInsightWeekIndex];
    const currentInsightIndex = weekInsights.findIndex((insight) => insight.id === selectedInsight.id);

    const isFirstWeek = currentInsightWeekIndex === 0;
    const isFirstWeekInsight = currentInsightIndex === 0;

    // first insight from the week and has no more previous weeks
    if (isFirstWeekInsight && isFirstWeek) return;

    // first insight from week, but still have more
    if (isFirstWeekInsight) {
      const previousWeekInsights = allInsights[currentInsightWeekIndex - 1];
      setSelectedInsight(previousWeekInsights[previousWeekInsights.length - 1]);
      const updatedReadInsights = markInsightAsRead(previousWeekInsights[previousWeekInsights.length - 1]);
      setReadInsights(updatedReadInsights);
      return;
    }

    // has more insights on current week
    const previousInsight = weekInsights[currentInsightIndex - 1];
    setSelectedInsight(previousInsight);
    const updatedReadInsights = markInsightAsRead(previousInsight);
    setReadInsights(updatedReadInsights);

    // is first week and previous is first insight
    if (isFirstWeek && currentInsightIndex === 1) {
      setIsFirstInsight(true);
    }
  };

  const weekPeriods = getWeekRequestPeriods();
  const weekPeriodsIsoStrings = weekPeriods.map((week) => ({ from: week.from.toISOString(), to: week.to.toISOString() }));

  const insightsWeek0 = useGetInsights({ from: weekPeriodsIsoStrings[0].from, to: weekPeriodsIsoStrings[0].to });
  const insightsWeek1 = useGetInsights({ from: weekPeriodsIsoStrings[1].from, to: weekPeriodsIsoStrings[1].to });
  const insightsWeek2 = useGetInsights({ from: weekPeriodsIsoStrings[2].from, to: weekPeriodsIsoStrings[2].to });
  const insightsWeek3 = useGetInsights({ from: weekPeriodsIsoStrings[3].from, to: weekPeriodsIsoStrings[3].to });

  const allInsights = [
    insightsWeek0.data?.filter((i) => i.impact) || [],
    insightsWeek1.data?.filter((i) => i.impact) || [],
    insightsWeek2.data?.filter((i) => i.impact) || [],
    insightsWeek3.data?.filter((i) => i.impact) || [],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <Dialog.Content size='lg' className='flex h-[650px] flex-row gap-0 p-0'>
        <div className='box-content w-80 overflow-y-auto bg-gray-2 p-6'>
          <Typography className='mb-6' variant='heading-2' color='secondary'>
            Histórico de Insights
          </Typography>
          {allInsights.map((insights, index) => (
            <WeekInsights key={index} insights={insights} selectedInsight={selectedInsight} onInsightClick={handleInsightClick} readInsights={readInsights} />
          ))}
        </div>
        <div className='flex w-fit flex-col'>
          <Dialog.Header className='pt-5'>
            <div className='flex flex-row items-center gap-2'>
              <Icon name='sparkles' color='primary' />
              <Typography variant='heading-2' color='secondary'>
                Cobli IA
              </Typography>
              <Tag color='primary' variant='outlined' className='ml-2' label='Beta' />
            </div>
            <Dialog.Close />
          </Dialog.Header>
          <Dialog.Body className='flex h-full flex-col gap-10 overflow-y-auto px-6 pt-0'>
            {!selectedInsight && <LandingContent />}
            {selectedInsight && (
              <InsightContent
                insight={selectedInsight}
                isFirstInsight={isFirstInsight}
                isLastInsight={isLastInsight}
                onPreviousInsightClick={handlePreviousInsightClick}
                onNextInsightClick={handleNextInsightClick}
              />
            )}
          </Dialog.Body>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default InsightsModal;
